import React from 'react';
import { getIntl } from '../../../../../../utils/intl';
import { useProfilesTabNavigation } from '../../../../../../utils/appNavigationNames';
import { PermissionDomainCheck } from '../../../../../v2BaseComponents/Permissions/PermissionCheck';
import { MedicalDataTypes } from '../../../../../../primary-record-types';
import RefreshProgress from '../../../../../shared/RefreshProgress';
import { isPresent } from 'ts-is-present';
import CardButton, { CARD_BUTTON_SIZE } from '../../../../../v2BaseComponents/CardButton';
import _chunk from 'lodash/chunk';
import Stack from '../../../../../v2BaseComponents/Stack';
import SizedView from '../../../../../v2BaseComponents/SizedView';
import { View } from 'react-native';
import { useProfileCounts } from '../../../../../../hooks/useProfileCounts';

const intl = getIntl('ProfileCardButtons', {
    medication: 'Medications',
    condition: 'Conditions',
    allergy: 'Allergies',
    visit: 'Visits',
    labs: 'Labs',
    imaging: 'Imaging',
    procedure: 'Procedures',
    vaccination: 'Vaccinations',
    careTeam: 'Care Team',
    profileInfo: 'Profile Info',
    activeCount: 'Active',
    upcomingCount: 'Upcoming',
    totalCount: 'Total',
    reports: 'Reports',
    files: 'Files',
    chat: 'Chat Assistant',
    lab: 'Labs',
    chatSubtext: '(Beta)',
    activity: 'Activity',
    restricted: 'Restricted',
    portals: 'Portals',
});

export type ProfileCardButtonsOwnProps = {
    profileId: string;
};

export type ProfileCardButtonsProps = ProfileCardButtonsOwnProps;

const MIN_CARD_PADDING = 10;
const MAX_CARD_PADDING = 24;

const ProfileCardButtons: React.FC<ProfileCardButtonsProps> = ({ profileId }) => {
    const profileCounts = useProfileCounts({ profileId });
    const profilesNavigation = useProfilesTabNavigation();

    const getCategoryLabel = (category: MedicalDataTypes.PrimaryRecordResourceCategory) => {
        return intl(category);
    };

    const getChunkedItems = (
        items: React.ReactNode[],
        width: number,
    ): { chunks: (React.ReactNode | null)[][]; padding: number } => {
        const getCount = () => {
            const getRenderedWidthForCount = (count: number) =>
                count * CARD_BUTTON_SIZE + (count - 1) * MIN_CARD_PADDING;
            let count = 10;
            do {
                count--;
            } while (getRenderedWidthForCount(count) > width && count > 1);

            return count;
        };

        const chunkSize = getCount();

        const chunks = _chunk(items, chunkSize).map((chunk) =>
            chunk.length < chunkSize ? [...chunk, ...Array(chunkSize - chunk.length).fill(null)] : chunk,
        ) as (React.ReactNode | null)[][];

        const maybeTooLargePadding = (width - chunkSize * CARD_BUTTON_SIZE) / (chunkSize - 1);

        return { chunks, padding: Math.min(maybeTooLargePadding, MAX_CARD_PADDING) };
    };

    const items: React.ReactNode[] = [
        <PermissionDomainCheck
            medicalProfileId={profileId}
            domain="patientPortals"
            permission="view"
            renderCallback={(hasAccess) => (
                <CardButton
                    isDisabled={!hasAccess}
                    icon="Portals"
                    title={intl('portals')}
                    onPress={() => {
                        profilesNavigation.navigate('connect', {
                            profileId,
                        });
                    }}
                    testID="card-button-portals"
                    subText={profileCounts.portals}
                />
            )}
        />,
        <PermissionDomainCheck
            medicalProfileId={profileId}
            domain="medications"
            permission="view"
            renderCallback={(hasAccess) => (
                <CardButton
                    isDisabled={!hasAccess}
                    icon="Medications"
                    title={getCategoryLabel('medication')}
                    onPress={() => {
                        profilesNavigation.navigate('data', { profileId, category: 'medication' });
                    }}
                    subText={profileCounts.medication}
                    testID="card-button-medications"
                />
            )}
        />,
        <PermissionDomainCheck
            medicalProfileId={profileId}
            domain="conditions"
            permission="view"
            renderCallback={(hasAccess) => (
                <CardButton
                    isDisabled={!hasAccess}
                    icon="Conditions"
                    title={getCategoryLabel('condition')}
                    onPress={() => {
                        profilesNavigation.navigate('data', { profileId, category: 'condition' });
                    }}
                    subText={profileCounts.condition}
                    testID="card-button-conditions"
                />
            )}
        />,
        <PermissionDomainCheck
            medicalProfileId={profileId}
            domain="visits"
            permission="view"
            renderCallback={(hasAccess) => (
                <CardButton
                    isDisabled={!hasAccess}
                    icon="Visits"
                    title={getCategoryLabel('visit')}
                    onPress={() => {
                        profilesNavigation.navigate('data', { profileId, category: 'visit' });
                    }}
                    subText={profileCounts.visit}
                    testID="card-button-visits"
                />
            )}
        />,
        <PermissionDomainCheck
            medicalProfileId={profileId}
            domain="allergies"
            permission="view"
            renderCallback={(hasAccess) => (
                <CardButton
                    isDisabled={!hasAccess}
                    icon="Allergies"
                    title={getCategoryLabel('allergy')}
                    onPress={() => {
                        profilesNavigation.navigate('data', { profileId, category: 'allergy' });
                    }}
                    subText={profileCounts.allergy}
                    testID="card-button-allergies"
                />
            )}
        />,
        /*
        <PermissionDomainCheck
            medicalProfileId={profileId}
            domain="imaging"
            permission="view"
            renderCallback={(hasAccess) => (
                <CardButton
                    isDisabled={!hasAccess}
                    icon="Imaging"
                    title={getCategoryLabel('imaging')}
                    onPress={() => {
                        profilesNavigation.navigate('data', { profileId, category: 'imaging' });
                    }}
                    subText={getSubLabel('imaging')}
                    testID="card-button-imaging"
                />
            )}
        />,
        */
        <PermissionDomainCheck
            medicalProfileId={profileId}
            domain="procedures"
            permission="view"
            renderCallback={(hasAccess) => (
                <CardButton
                    isDisabled={!hasAccess}
                    icon="Procedures"
                    title={getCategoryLabel('procedure')}
                    onPress={() => {
                        profilesNavigation.navigate('data', { profileId, category: 'procedure' });
                    }}
                    subText={profileCounts.procedure}
                    testID="card-button-procedures"
                />
            )}
        />,
        <PermissionDomainCheck
            medicalProfileId={profileId}
            domain="vaccinations"
            permission="view"
            renderCallback={(hasAccess) => (
                <CardButton
                    isDisabled={!hasAccess}
                    icon="Vaccinations"
                    title={getCategoryLabel('vaccination')}
                    onPress={() => {
                        profilesNavigation.navigate('data', { profileId, category: 'vaccination' });
                    }}
                    subText={profileCounts.vaccination}
                    testID="card-button-vaccinations"
                />
            )}
        />,
        <PermissionDomainCheck
            medicalProfileId={profileId}
            domain="labs"
            permission="view"
            renderCallback={(hasAccess) => (
                <CardButton
                    isDisabled={!hasAccess}
                    icon="Labs"
                    title={intl('labs')}
                    onPress={() => {
                        profilesNavigation.navigate('data', { profileId, category: 'lab' });
                    }}
                    subText={profileCounts.lab}
                    testID="card-button-labs"
                />
            )}
        />,
        <PermissionDomainCheck
            medicalProfileId={profileId}
            domain="careTeam"
            permission="view"
            renderCallback={(hasAccess) => (
                <CardButton
                    isDisabled={!hasAccess}
                    icon="CareTeam"
                    title={intl('careTeam')}
                    onPress={() => {
                        profilesNavigation.navigate('care-team', {
                            profileId,
                        });
                    }}
                    subText={profileCounts.careTeam}
                    testID="card-button-care-team"
                />
            )}
        />,
        <PermissionDomainCheck
            medicalProfileId={profileId}
            domain="reports"
            permission="view"
            renderCallback={(hasAccess) => (
                <CardButton
                    isDisabled={!hasAccess}
                    icon="Reports"
                    title={intl('reports')}
                    onPress={() => {
                        profilesNavigation.navigate('reports', {
                            profileId,
                        });
                    }}
                    testID="card-button-reports"
                />
            )}
        />,
        <PermissionDomainCheck
            medicalProfileId={profileId}
            domain="files"
            permission="view"
            renderCallback={(hasAccess) => (
                <CardButton
                    isDisabled={!hasAccess}
                    icon="Documents"
                    title={intl('files')}
                    onPress={() => {
                        profilesNavigation.navigate('files', {
                            profileId,
                        });
                    }}
                    testID="card-button-files"
                />
            )}
        />,
        <CardButton
            icon="ProfileInfo"
            title={intl('profileInfo')}
            onPress={() => {
                profilesNavigation.navigate('edit', {
                    profileId,
                    firstName: '',
                });
            }}
            testID="card-button-profile-info"
        />,
    ].filter(isPresent);

    return (
        <Stack vertical space={2} testID="profile-root-card-container" align="center" fullWidth>
            <PermissionDomainCheck
                medicalProfileId={profileId}
                domain="patientPortals"
                permission="view"
                renderCallback={() => <RefreshProgress medicalProfileId={profileId} minimal />}
            />
            <SizedView style={{ flex: 1, alignSelf: 'stretch' }}>
                {({ width }) => {
                    const { chunks, padding } = getChunkedItems(items, width);

                    return chunks.map((chunk, index) => (
                        <Stack horizontal fullWidth key={index} justify="center" style={{ marginBottom: padding }}>
                            {chunk.map((item, index) => (
                                <View
                                    key={index}
                                    style={{
                                        marginRight: index === chunk.length - 1 ? 0 : padding,
                                    }}
                                >
                                    {item || <View style={{ width: CARD_BUTTON_SIZE }} />}
                                </View>
                            ))}
                        </Stack>
                    ));
                }}
            </SizedView>
        </Stack>
    );
};

export default React.memo(ProfileCardButtons);
